import React, { useEffect, useState } from "react";
import { FaUserClock, FaExternalLinkAlt } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import DashboardCard from "./DashboardCard";
import api from "../../../services/api";
import Loader from "../../../components/loader";
import { getUserMonthlyOccupation, getAggregatedOccupationsForMonth, filterOccupationsForMonth, mapActivitiesToOccupations } from "../../../utils/occupation";

const PROJECT_COLORS = [
  "#4f46e5", // Indigo
  "#06b6d4", // Cyan
  "#8b5cf6", // Purple
  "#ec4899", // Pink
  "#f97316", // Orange
  "#14b8a6", // Teal
  "#8b5cf6", // Violet
  "#f43f5e", // Rose
  "#10b981", // Emerald
  "#6366f1", // Indigo (slightly different shade)
];

const TeamOccupationCard = ({ user, filters, businessUnit }) => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [occupations, setOccupations] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const openUserOccupation = (memberId) => {
    const params = new URLSearchParams(location.search);
    params.set("user_modal_id", memberId);
    params.set("user_modal_tab", "activity");
    navigate({ pathname: location.pathname, search: params.toString() });
  };

  const months = [...Array(2)].map((_, i) => {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() + i);
    return date;
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Build user search query
        const userQuery = {
          role: ["admin", "user", "super_admin"],
          contracts: ["VIE", "CDD", "CDI", "APPRENTICESHIP", "INTERNSHIP", "FREELANCE", "Me kohe te plote"],
          status: "active",
        };

        // Add manager filter only if a user is selected
        if (user) {
          userQuery.manager_id = user._id;
          userQuery.managees_of_managees = filters.manageesOfManagees;
        }

        // Filter by business unit
        if (businessUnit._id !== "ALL") {
          userQuery.business_unit_id = businessUnit._id;
        }

        // Fetch team members
        const { data: usersData, ok } = await api.post(`/user/search`, userQuery);

        if (!ok || !usersData?.users) {
          console.error("Error fetching users for team occupation");
          setLoading(false);
          return;
        }

        // Filter out support functions and incoming users, but keep daily freelancers
        const today = new Date();
        const filteredUsers = usersData.users.filter((u) => {
          if (u.non_billable) return false;
          // Exclude users who haven't started yet
          if (u.starting_date && new Date(u.starting_date) > today) return false;
          return true;
        });

        // Fetch forecasted activities scoped to these users
        const userIds = filteredUsers.map((u) => u._id);
        let occupationData = [];
        if (userIds.length > 0) {
          const dateFrom = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
          const { data: activities } = await api.post("/activity/search", { userIds, has_forecast: true, dateFrom });
          occupationData = mapActivitiesToOccupations(activities);
        }
        setOccupations(occupationData);

        // Sort team members by current month's occupation (ascending - lowest first = needs work)
        const currentMonth = new Date();
        const sortedUsers = filteredUsers.map((u) => {
          const currentMonthOccupation = getUserMonthlyOccupation(occupationData, u._id, currentMonth);
          const isIncoming = u.starting_date && new Date(u.starting_date) > currentMonth;
          return { ...u, currentMonthOccupation, isIncoming };
        });
        // Sort by occupation ascending (lowest first = needs attention)
        sortedUsers.sort((a, b) => a.currentMonthOccupation - b.currentMonthOccupation);
        setTeamMembers(sortedUsers);
      } catch (error) {
        console.error("Error fetching team occupation data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?._id, filters, businessUnit]);

  const handleEditClick = () => {
    navigate("/brain");
  };

  if (loading) return <Loader />;

  // Exclude incoming users and split by contract type
  const activeMembers = teamMembers.filter((m) => !m.isIncoming);
  const regularMembers = activeMembers.filter((m) => !(m.contract === "FREELANCE" && m.payment_type === "DAILY"));
  const dailyFreelancers = activeMembers.filter((m) => m.contract === "FREELANCE" && m.payment_type === "DAILY");

  const renderTable = (members, title) => {
    // Exclude incoming users from calculations
    const activeMembers = members.filter((m) => !m.isIncoming);
    
    return (
    <div className="overflow-x-auto">
      {title && <div className="text-xs font-medium text-gray-500 mb-1 mt-3">{title}</div>}
      <table className="w-full">
        <thead>
          <tr className="border-b">
            <th className="text-left p-1 text-xs w-[120px]">Person</th>
            {months.map((month) => {
              const totalPercentage = activeMembers.length > 0 
                ? activeMembers.reduce((sum, member) => sum + getUserMonthlyOccupation(occupations, member._id, month), 0) / activeMembers.length 
                : 0;

              return (
                <th key={month.toString()} className="text-left p-1 text-xs min-w-[100px]">
                  <div className="text-gray-400 mb-1">{totalPercentage.toFixed(0)}%</div>
                  {month.toLocaleString("default", { month: "short", year: "numeric" })}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {members.map((member) => {
            const isInactive = member.exit_date && new Date() > new Date(member.exit_date);
            return (
              <tr 
                key={member._id} 
                className={`border-b hover:bg-gray-50 cursor-pointer ${isInactive ? "bg-gray-100" : ""}`}
                onClick={() => openUserOccupation(member._id)}>
                <td className="p-1">
                  <div className="flex items-center gap-2">
                    <img src={member.avatar} alt={member.name} className="w-6 h-6 rounded-full object-cover" />
                    <div>
                      <div className="text-sm font-medium">{member.name}</div>
                      <div className="text-[10px] text-gray-400">
                        {member.position} • {member.contract}
                      </div>
                    </div>
                  </div>
                </td>
                {months.map((month) => (
                  <td key={month.toString()} className="p-1">
                    <OccupationBar
                      user={member}
                      month={month}
                      occupations={filterOccupationsForMonth(occupations, member._id, month)}
                    />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
  };

  return (
    <DashboardCard
      title="Occupation"
      icon={<FaUserClock className="text-orange-500" />}
      action={
        <button onClick={handleEditClick} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors">
          <FaExternalLinkAlt className="w-3 h-3" />
          Edit
        </button>
      }>
      <div className="space-y-2">
        {activeMembers.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            <p className="text-sm">No team members found</p>
          </div>
        ) : (
          <>
            {regularMembers.length > 0 && renderTable(regularMembers, null)}
            {dailyFreelancers.length > 0 && (
              <div className="mt-4 pt-3 border-t border-dashed border-gray-300">
                <div className="text-xs font-medium text-orange-600 mb-2 flex items-center gap-1">
                  <span>📅</span> Daily Freelancers
                </div>
                {renderTable(dailyFreelancers, null)}
              </div>
            )}
          </>
        )}
      </div>
    </DashboardCard>
  );
};

const OccupationBar = ({ user, month, occupations }) => {
  const isInactive = user?.exit_date && new Date(month).getTime() > new Date(user.exit_date).getTime();
  const incoming =
    user?.starting_date &&
    (new Date(month).getFullYear() < new Date(user.starting_date).getFullYear() ||
      (new Date(month).getFullYear() === new Date(user.starting_date).getFullYear() && new Date(month).getMonth() < new Date(user.starting_date).getMonth()));

  if (isInactive) return <div className="h-4 w-full bg-gray-50 flex items-center justify-center text-gray-400 text-[8px] font-medium">inactive</div>;
  if (incoming) return <div className="h-4 w-full bg-gray-50 flex items-center justify-center text-gray-400 text-[8px] font-medium">incoming</div>;

  // Properly weights weekly entries by total weeks in month
  const aggregatedOccupations = getAggregatedOccupationsForMonth(occupations, month);
  const totalOccupancy = aggregatedOccupations.reduce((sum, o) => sum + o.percent, 0);
  const isOverallocated = totalOccupancy > 100;

  return (
    <div className="relative group">
      <div className={`h-4 w-full bg-gray-50 rounded-sm flex ${isOverallocated ? "ring-1 ring-red-500" : ""}`}>
        {aggregatedOccupations.map((occupation, index) => (
          <div
            key={occupation.project_id || occupation.project_name}
            className="h-full transition-opacity hover:opacity-100 relative flex items-center justify-center flex-shrink-0"
            style={{
              width: `${(occupation.percent / Math.max(totalOccupancy, 100)) * 100}%`,
              backgroundColor: PROJECT_COLORS[index % PROJECT_COLORS.length],
              backgroundImage: occupation.soft ? "repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.2) 3px, rgba(255,255,255,0.2) 6px)" : "none",
              opacity: occupation.hasWeekly ? 0.95 : 0.85,
              border: occupation.hasWeekly ? "1px solid rgba(168, 85, 247, 0.5)" : "none",
            }}
            title={
              occupation.isAveraged
                ? `${occupation.project_name}: ${occupation.percent.toFixed(0)}% (avg over ${occupation.totalWeeks} weeks, ${occupation.weeksCount} with entries)`
                : `${occupation.project_name}: ${occupation.percent.toFixed(0)}%`
            }>
            <span className="text-[8px] text-white font-medium z-10 absolute whitespace-nowrap">{occupation.percent.toFixed(0)}%</span>
          </div>
        ))}
      </div>
      {isOverallocated && (
        <div className="absolute left-0 -top-6 bg-white/90 border border-red-500 rounded px-1 text-[8px] text-red-500 whitespace-nowrap z-10">{totalOccupancy.toFixed(0)}%</div>
      )}
    </div>
  );
};

export default TeamOccupationCard;
